import Link from 'next/link';
import { ReactNode } from 'react';
import { IoQrCodeOutline } from 'react-icons/io5';

import { Anchor, Box, Group, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core';
import { unique } from 'radash';

import { CompanyProfile, Event, QrScanPayload, QrScanType } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { ComponentHelpers } from 'helpers/ComponentHelpers';
import { EventHelpers } from 'helpers/EventHelpers';
import { ShowtimeHelpers } from 'helpers/ShowtimeHelpers';

import { CollateralCover } from 'components/collateral/CollateralCover';
import { Logo } from 'components/company-profile-teaser/components/Logo';
import { DashboardPanel } from 'components/dashboard-panel/DashboardPanel';
import { IKImage } from 'components/ik-image/IKImage';
import { ReplusLogo } from 'components/logo/ReplusLogo';
import { useShowtimeEvent } from 'components/showtime/hooks/use-showtime-event';
import { useSavedProfiles } from 'components/showtime/hooks/useSavedProfiles';
import { ComponentThumbnail } from 'components/thumbnail/ComponentThumbnail';
import { useCollateralItem } from 'hooks/use-collateral-item';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useComponent } from 'hooks/use-component';
import { DateService } from 'services/DateService';

import cx from './DashboardShowtime.module.scss';

const DashboardShowtime = () => {
    const { qrScans } = useSavedProfiles();
    const event = useShowtimeEvent();

    if (!qrScans.length) {
        return null;
    }

    return (
        <DashboardPanel
            shadedHeader
            live={Boolean(event && EventHelpers.isHappeningNow(event))}
            header={<ShowtimeHeader event={event} />}
        >
            <ShowtimeScanTiles />
        </DashboardPanel>
    );
};

const ShowtimeScanTiles = ({ asBand }: { asBand?: boolean }) => {
    const { qrScans } = useSavedProfiles();

    if (!qrScans.length) {
        return null;
    }

    const itemKey = (scan: QrScanPayload) => scan.collateral ?? scan.product ?? scan.company;
    const latestScans = unique(
        [...qrScans].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
        itemKey,
    ).slice(0, 6);

    return (
        <Stack gap="md" className={asBand ? cx.band : undefined}>
            <Group justify="space-between">
                <Group gap="sm" wrap="nowrap">
                    <ThemeIcon variant="light" color="violet" size="md" radius="sm" fz={16}>
                        <IoQrCodeOutline />
                    </ThemeIcon>
                    <Text fw={700}>Your RE+ Showtime</Text>
                </Group>
                <Anchor component={Link} href={ShowtimeHelpers.urls.showtime()} fz="sm" fw={600}>
                    View all
                </Anchor>
            </Group>
            <Box className={cx.tiles}>
                {latestScans.map((scan) => (
                    <ScanTile key={scan.id} scan={scan} />
                ))}
            </Box>
        </Stack>
    );
};

const ShowtimeHeader = ({ event }: { event?: Event }) => {
    const headline = () => {
        if (!event) {
            return 'RE+ Showtime';
        }

        if (EventHelpers.isHappeningNow(event)) {
            return `${event.name} is happening now`;
        }

        return EventHelpers.isUpcoming(event)
            ? `${event.name} starts ${DateService.formatDistanceToNow(event.start)}`
            : `${event.name} wrapped up`;
    };

    return (
        <Group gap="md" wrap="nowrap" miw={0}>
            <Box className={cx.eventImage} bg={event?.background || 'white'}>
                {event?.backgroundImage ? (
                    <IKImage fileOrId={event.backgroundImage} width={192} height={128} alt={event.name} />
                ) : (
                    <ReplusLogo w={48} />
                )}
            </Box>
            <Stack gap={4} miw={0}>
                <Text fw={700} size="lg" lh={1.2}>
                    {headline()}
                </Text>
                {event && (
                    <Group gap={6} wrap="nowrap">
                        <Text size="sm" c="dimmed">
                            {EventHelpers.formatDate(event)}
                        </Text>
                        <Text size="sm" c="dimmed">
                            ·
                        </Text>
                        <Anchor component={Link} href={EventHelpers.urls.view(event.slug)} fw={500} fz="sm">
                            View event
                        </Anchor>
                    </Group>
                )}
            </Stack>
        </Group>
    );
};

const ScanTile = ({ scan }: { scan: QrScanPayload }) => {
    const { company, isLoading } = useCompanyProfile(scan.company);

    if (!company) {
        return isLoading ? <Skeleton className={cx.preview} radius="md" /> : null;
    }

    if (scan.type === QrScanType.COLLATERAL) {
        return <CollateralTile scan={scan} company={company} />;
    }

    if (scan.type === QrScanType.PRODUCT) {
        return <ProductTile scan={scan} company={company} />;
    }

    return (
        <Tile
            href={CompanyHelpers.urls.view(company.slug ?? company.id)}
            title={company.name}
            preview={<Logo company={company} fullWidth />}
            fit="contain"
        />
    );
};

const CollateralTile = ({ scan, company }: { scan: QrScanPayload; company: CompanyProfile }) => {
    const { collateral, href } = useCollateralItem(scan.collateral ?? '');

    return (
        <Tile
            href={href || CompanyHelpers.urls.view(company.slug ?? company.id)}
            title={collateral?.title ?? company.name}
            preview={collateral && <CollateralCover collateral={collateral} width={200} alt="" iconSize={24} />}
        />
    );
};

const ProductTile = ({ scan, company }: { scan: QrScanPayload; company: CompanyProfile }) => {
    const { component } = useComponent(scan.product);

    return (
        <Tile
            href={ComponentHelpers.urls.view(scan.product ?? '')}
            title={component?.name ?? company.name}
            preview={<ComponentThumbnail component={component} showEmpty style={{ width: '100%', height: '100%' }} />}
        />
    );
};

const Tile = ({
    href,
    title,
    preview,
    fit = 'cover',
}: {
    href: string;
    title: string;
    preview: ReactNode;
    fit?: 'cover' | 'contain';
}) => (
    <Box component={Link} href={href} className={cx.tile}>
        <Box className={cx.preview} data-fit={fit}>
            {preview}
        </Box>
        <Text fz="xs" fw={500} c="dimmed" mt={6} truncate>
            {title}
        </Text>
    </Box>
);

export { DashboardShowtime, ShowtimeScanTiles };
